import { useState, type ReactNode } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import {
  CalendarBlank,
  CaretRight,
  ChartLine,
  ChatCircleDots,
  CreditCard,
  FolderSimple,
  ImageSquare,
  Lock,
  Megaphone,
  PencilSimple,
  Plus,
  ShieldCheck,
  Sparkle,
  type Icon as PhosphorIcon,
} from '@phosphor-icons/react';
import type { BusinessDetail } from '@mybusinessmate/schema';
import { AssignAmDialog } from '../../../../../../components/businesses/assign-am-dialog';
import { BusinessHeader } from '../../../../../../components/businesses/business-header';
import { BusinessBreadcrumb, BusinessTabs } from '../../../../../../components/businesses/business-tabs';
import { Icon } from '../../../../../../components/Icon';
import { StaffBanner } from '../../../../../../components/shell/staff-banner';
import { Card } from '../../../../../../components/ui/card';
import { PageSkeleton } from '../../../../../../components/ui/skeleton';
import { LoadFailure } from '../../../../../../components/ui/state-views';
import { StatusTag } from '../../../../../../components/ui/status-tag';
import { useToast } from '../../../../../../components/ui/toast';
import { pillClass } from '../../../../../../components/ui/toolbar-controls';
import { textLinkClass } from '../../../../../../components/ui/text-link';
import { businessQuery } from '../../../../../../lib/directory-api';
import { requireBusinessAccess } from '../../../../../../lib/guards';
import { cx } from '../../../../../../lib/cx';
import { initialsOf } from '../../../../../../lib/initials';
import { formatDayYear } from '../../../../../../lib/project-display';
import { useSession } from '../../../../../../lib/session';
import { dueLabel, staffStatus, TASK_TYPE_META, weekdayDay } from '../../../../../../lib/task-display';
import { toasts } from '../../../../../../lib/toast-presets';

export const Route = createFileRoute('/subdomain/admin/_staff/businesses/$businessId/')({
  beforeLoad: ({ context }) => requireBusinessAccess(context.queryClient),
  // The page is 1200 wide in the design (same canvas as the tables).
  staticData: { wide: true },
  component: BusinessPage,
});

const HEAD = 'text-label-small text-text-tertiary uppercase';

/** The card title of the business pages: Heading/H4, with an optional muted line under it. */
function CardTitle({ title, sub }: { title: ReactNode; sub?: ReactNode }) {
  return (
    <div className="flex flex-col gap-(--mbm-space-0-5)">
      <h2 className="flex items-center gap-(--mbm-space-2) text-heading-h4 text-text-strong">{title}</h2>
      {sub ? <p className="text-body-xsmall text-text-tertiary">{sub}</p> : null}
    </div>
  );
}

function Fact({ label, children }: { label: string; children?: ReactNode }) {
  return (
    <div className="flex flex-col gap-(--mbm-space-0-5) py-(--mbm-space-2)">
      <dt className="text-caption text-text-tertiary">{label}</dt>
      <dd className="text-body-medium text-text-primary">{children || '—'}</dd>
    </div>
  );
}

/** "36AABCP1234F1Z5" as "36AABCP••••1Z5": the middle of a tax id is not for the screen. */
const maskGstin = (gstin: string | null) =>
  gstin && gstin.length >= 12 ? `${gstin.slice(0, 7)}••••${gstin.slice(-3)}` : gstin;

/** One figure of "This billing cycle": a small label, a big value, and a note. */
function Stat({
  label,
  value,
  note,
  first = false,
}: {
  label: string;
  value: ReactNode;
  note?: ReactNode;
  first?: boolean;
}) {
  return (
    <div
      className={cx(
        'flex min-w-0 flex-1 flex-col gap-(--mbm-space-0-75) pr-(--mbm-space-3)',
        first ? '' : 'border-l border-border-default pl-(--mbm-space-5)',
      )}
    >
      <span className="text-body-xsmall text-text-secondary">{label}</span>
      <span className="text-heading-h2 text-text-strong">{value}</span>
      {note ? (
        <span className="text-caption text-text-tertiary">{note}</span>
      ) : (
        <span className="h-(--mbm-space-3-5)" />
      )}
    </div>
  );
}

/**
 * Figma A03.2 "This billing cycle": the open work, revisions, next meeting and plan, and the month's quotas.
 * TODO(api): everything but the open work needs billing (S4), bookings (S3) or review versions (S2-03). The card is
 * drawn as designed and says so with dashes instead of numbers the API cannot back.
 */
function BillingCycle({ business }: { business: BusinessDetail }) {
  const toast = useToast();
  const waiting = business.waitingForClientCount;
  return (
    <Card
      tone="panel"
      padding="none"
      className="flex flex-col gap-(--mbm-space-4-5) px-(--mbm-space-6) py-(--mbm-space-5-5)"
    >
      <div className="flex items-center justify-between gap-(--mbm-space-3)">
        <CardTitle title="This billing cycle" sub="Appears once billing is live" />
        <button
          type="button"
          onClick={() => toast(toasts.comingSoon('Billing arrives in Sprint 4.'))}
          className={cx(textLinkClass, 'inline-flex items-center gap-(--mbm-space-1)')}
        >
          Billing
          <Icon icon={CaretRight} size="xs" />
        </button>
      </div>
      <div className="flex">
        <Stat
          first
          label="Open work"
          value={business.counts.openTasks}
          note={waiting > 0 ? `${waiting} waiting for client approval` : 'Nothing waiting on the client'}
        />
        <Stat label="Revisions used" value="—" />
        <Stat label="Next meeting" value="—" />
        <Stat label="Plan" value="—" />
      </div>
      <div className="h-px w-full bg-border-line" />
      <div className="grid grid-cols-2 gap-(--mbm-space-6) sm:grid-cols-4">
        {['Posts', 'Reels', 'Website updates', 'Reports'].map((quota) => (
          <div key={quota} className="flex flex-col gap-(--mbm-space-2)">
            <div className="flex items-baseline justify-between">
              <span className="text-label-medium text-text-primary">{quota}</span>
              <span className="text-body-xsmall text-text-tertiary">—</span>
            </div>
            <div className="h-(--mbm-space-1-5) w-full rounded-full bg-surface-field" />
          </div>
        ))}
      </div>
    </Card>
  );
}

/** Everything a business has, as the design lists it. Only Projects & tasks has a page yet. */
const EVERYTHING: { label: string; icon: PhosphorIcon }[] = [
  { label: 'Deliverables & logins', icon: ImageSquare },
  { label: 'Audits & reports', icon: ChartLine },
  { label: 'Payments & invoices', icon: CreditCard },
  { label: 'Add-on orders', icon: Sparkle },
  { label: 'Meetings', icon: CalendarBlank },
  { label: 'Chat', icon: ChatCircleDots },
  { label: 'Leads', icon: Megaphone },
];

/** One row of "Everything for this client" (Figma A03.2): 42px, a 12px radius, the icon, the name and a quiet count. */
const JUMP =
  'flex h-(--mbm-size-jump-row) items-center gap-(--mbm-space-3) rounded-(--mbm-radius-sm) pl-(--mbm-space-3) pr-(--mbm-space-2-5) hover:bg-surface-muted focus-visible:outline-none focus-visible:shadow-(--mbm-shadow-focus-ring)';

const OPEN_WORK_GRID =
  'grid grid-cols-[minmax(0,250fr)_minmax(0,120fr)_minmax(0,150fr)_minmax(0,110fr)_minmax(0,140fr)] items-center';

function OpenWork({ business }: { business: BusinessDetail }) {
  const ownerFirstName = business.ownerName.split(/\s+/)[0] ?? 'the client';
  const projects = [...new Set(business.openWork.map((t) => t.projectName))];
  return (
    <Card
      tone="panel"
      padding="none"
      className="flex flex-col gap-(--mbm-space-3) px-(--mbm-space-6) pb-(--mbm-space-3) pt-(--mbm-space-5-5)"
    >
      <div className="flex items-center justify-between gap-(--mbm-space-3)">
        <CardTitle
          title="Open work"
          sub={
            business.counts.projects > 0
              ? `Across ${business.counts.projects} ${business.counts.projects === 1 ? 'project' : 'projects'}${projects.length > 0 ? ` · ${projects.slice(0, 3).join(', ')}` : ''}`
              : 'No projects yet'
          }
        />
        <Link
          to="/subdomain/admin/businesses/$businessId/projects"
          params={{ businessId: business.id }}
          search={{ bucket: 'active', domain: undefined, q: undefined }}
          className={cx(textLinkClass, 'inline-flex items-center gap-(--mbm-space-1)')}
        >
          All tasks
          <Icon icon={CaretRight} size="xs" />
        </Link>
      </div>

      {business.openWork.length === 0 ? (
        <p className="pb-(--mbm-space-3) text-body-medium text-text-secondary">Nothing open right now.</p>
      ) : (
        <div className="flex flex-col pb-(--mbm-space-2)">
          <div
            className={cx(
              OPEN_WORK_GRID,
              'h-(--mbm-size-table-head-sm) rounded-(--mbm-component-radius-rail-item) bg-surface-canvas',
            )}
          >
            <span className={cx(HEAD, 'pl-(--mbm-space-3)')}>Task</span>
            <span className={HEAD}>Project</span>
            <span className={HEAD}>Status</span>
            <span className={HEAD}>Owner</span>
            <span className={HEAD}>Due</span>
          </div>
          {business.openWork.map((task) => {
            const status = staffStatus(task.status);
            const due = dueLabel(task.deadline);
            const waiting = task.status === 'In Review';
            return (
              <Link
                key={task.id}
                to="/subdomain/admin/tasks/$taskId"
                params={{ taskId: task.id }}
                className={cx(
                  OPEN_WORK_GRID,
                  'min-h-(--mbm-size-table-row-sm) border-t border-border-line hover:bg-surface-muted focus-visible:outline-none focus-visible:shadow-(--mbm-shadow-focus-ring)',
                )}
              >
                <span className="flex min-w-0 flex-col gap-px pl-(--mbm-space-3)">
                  <span className="truncate text-label-medium text-text-primary">{task.title}</span>
                  <span className="truncate text-body-xsmall text-text-tertiary">
                    {TASK_TYPE_META[task.taskType].short}
                  </span>
                </span>
                <span className="truncate text-body-small text-text-primary">{task.projectName}</span>
                <span>
                  <StatusTag
                    size="md"
                    label={status.label}
                    tone={status.tone}
                    variant={status.tone === 'grey' ? 'outline' : 'soft'}
                  />
                </span>
                <span className="truncate text-body-small text-text-primary">{task.assignee ?? 'Unassigned'}</span>
                <span className="truncate text-body-small text-text-primary">
                  {waiting ? `Waiting for ${ownerFirstName}` : due ? weekdayDay(task.deadline!) : '—'}
                </span>
              </Link>
            );
          })}
        </div>
      )}
    </Card>
  );
}

/**
 * A business (Figma A03.2 "Overview").
 * TODO(api): the design's "This billing cycle" card (billing S4), the plan and Active chips, next meeting, GBP review
 * stats and "last changed by" line have no data yet. They are left out of the layout, not faked.
 */
function BusinessPage() {
  const { businessId } = Route.useParams();
  const toast = useToast();
  const { data: session } = useSession();
  const isAdmin = session?.user.role === 'admin';
  const [assigning, setAssigning] = useState(false);
  const { data: business, isLoading, error, refetch } = useQuery(businessQuery(businessId));
  const soon = () => toast(toasts.comingSoon('This arrives with its own sprint.'));

  if (isLoading) return <PageSkeleton />;

  if (!business) {
    return (
      <LoadFailure
        error={error}
        noun="business"
        forbiddenHint="Ask an admin to assign it to you."
        action={
          <Link
            to="/subdomain/admin/businesses"
            search={{ tab: undefined, q: undefined, am: undefined }}
            className="text-label-large text-brand-primary"
          >
            Back to businesses
          </Link>
        }
        onRetry={() => void refetch()}
      />
    );
  }

  const primary = business.accountManagers.find((m) => m.isPrimary) ?? business.accountManagers[0];
  const address = [business.addressLine1, business.city, business.state, business.postalCode]
    .filter(Boolean)
    .join(', ');
  // An admin looking at someone else's client is working "as an override": the design says so, and every change is audited.
  const override = isAdmin && !!primary && primary.id !== session?.user.id;

  return (
    <>
      {override ? (
        <StaffBanner>
          <div className="flex h-(--mbm-size-banner) items-center justify-between gap-(--mbm-space-4) bg-action-primary px-(--mbm-space-4) text-body-small text-text-on-dark lg:px-(--mbm-space-7)">
            <span className="flex items-center gap-(--mbm-space-2-5)">
              <Icon icon={ShieldCheck} size="sm" />
              <span>
                <strong className="font-semibold">Admin override</strong> · This is {primary.name}’s client. You can
                make changes, and each one is logged as an override.
              </span>
            </span>
            <button
              type="button"
              onClick={soon}
              className="font-semibold focus-visible:outline-none focus-visible:shadow-(--mbm-shadow-focus-ring)"
            >
              View audit trail
            </button>
          </div>
        </StaffBanner>
      ) : null}

      <BusinessBreadcrumb businessName={business.name} />

      <BusinessHeader
        business={business}
        primary={
          <Link
            to="/subdomain/admin/businesses/$businessId/projects"
            params={{ businessId: business.id }}
            search={{ bucket: 'active', domain: undefined, q: undefined }}
            className={pillClass('dark', 'lg')}
          >
            <Icon icon={Plus} size="sm" />
            New task
          </Link>
        }
      />

      <BusinessTabs
        businessId={business.id}
        active="overview"
        projectCount={business.counts.projects}
        deliverableCount={business.counts.deliverables}
      />

      <div className="grid items-start gap-(--mbm-space-5) lg:grid-cols-[minmax(0,1fr)_minmax(0,22.5rem)]">
        <div className="flex flex-col gap-(--mbm-space-5)">
          <BillingCycle business={business} />
          <OpenWork business={business} />

          <Card
            tone="panel"
            padding="none"
            className="flex flex-col gap-(--mbm-space-1-5) px-(--mbm-space-5) pb-(--mbm-space-4) pt-(--mbm-space-5)"
          >
            <div className="flex items-center justify-between gap-(--mbm-space-3) pb-(--mbm-space-2)">
              <h2 className="flex items-center gap-(--mbm-space-2) text-heading-h4 text-text-strong">
                Business details
                <Icon icon={Lock} size="sm" />
              </h2>
              <button type="button" onClick={soon} className={pillClass('light')}>
                <Icon icon={PencilSimple} size="sm" />
                Edit
              </button>
            </div>
            <dl className="grid gap-x-(--mbm-space-6) sm:grid-cols-3">
              <Fact label="Legal name">{business.name}</Fact>
              <Fact label="GSTIN">{[maskGstin(business.gstNumber), business.state].filter(Boolean).join(' · ')}</Fact>
              <Fact label="Owner">{business.ownerName}</Fact>
              <Fact label="Contact phone">{business.contactPhone}</Fact>
              <Fact label="Contact email">{business.contactEmail ?? business.ownerEmail}</Fact>
              <Fact label="Website">{business.websiteUrl}</Fact>
              <Fact label="Address">{address}</Fact>
              <Fact label="Google Business Profile">{business.googleMapsUri ? 'Linked' : 'Not linked'}</Fact>
              <Fact label="Industry">{business.industry}</Fact>
            </dl>
          </Card>
        </div>

        <div className="flex flex-col gap-(--mbm-space-5)">
          <Card
            tone="panel"
            padding="none"
            className="flex flex-col gap-(--mbm-space-3) px-(--mbm-space-5) pb-(--mbm-space-4-5) pt-(--mbm-space-5)"
          >
            <div className="flex items-center justify-between gap-(--mbm-space-3)">
              <h2 className="text-heading-h4 text-text-strong">Account manager</h2>
              {isAdmin ? (
                <button type="button" onClick={() => setAssigning(true)} className={textLinkClass}>
                  {business.accountManagers.length > 0 ? 'Change' : 'Assign'}
                </button>
              ) : null}
            </div>
            {business.accountManagers.length === 0 ? (
              <p className="text-body-medium text-text-secondary">No account manager yet.</p>
            ) : (
              <ul className="flex flex-col gap-(--mbm-space-3)">
                {business.accountManagers.map((manager) => (
                  <li key={manager.id} className="flex items-center gap-(--mbm-space-3)">
                    <span className="flex size-(--mbm-size-avatar-md) shrink-0 items-center justify-center rounded-full bg-surface-field text-label-medium text-text-primary">
                      {initialsOf(manager.name)}
                    </span>
                    <span className="flex min-w-0 flex-col gap-(--mbm-space-0-5)">
                      <span className="text-label-medium text-text-primary">
                        {manager.name} · {manager.isPrimary ? 'Primary' : 'Backup'}
                      </span>
                      <span className="text-body-xsmall text-text-tertiary">Since {formatDayYear(manager.since)}</span>
                    </span>
                  </li>
                ))}
              </ul>
            )}
            {business.workingOnIt.length > 0 ? (
              <p className="flex gap-(--mbm-space-1-5) text-body-xsmall text-text-tertiary">
                Working on this client:
                <span className="text-text-secondary">{business.workingOnIt.join(', ')}</span>
              </p>
            ) : null}
          </Card>

          <Card
            tone="panel"
            padding="none"
            className="flex flex-col gap-(--mbm-space-1) px-(--mbm-space-2) pb-(--mbm-space-3-5) pt-(--mbm-space-5)"
          >
            <h2 className="pb-(--mbm-space-1-5) pl-(--mbm-space-3) text-heading-h4 text-text-strong">
              Everything for this client
            </h2>
            <Link
              to="/subdomain/admin/businesses/$businessId/projects"
              params={{ businessId: business.id }}
              search={{ bucket: 'active', domain: undefined, q: undefined }}
              className={JUMP}
            >
              <Icon icon={FolderSimple} size="md" />
              <span className="flex-1 text-label-medium text-text-primary">Projects & tasks</span>
              <span className="text-body-xsmall text-text-tertiary">
                {business.counts.openTasks} open · {business.counts.doneTasks} done
              </span>
              <Icon icon={CaretRight} size="xs" />
            </Link>
            {EVERYTHING.map(({ label, icon }) => (
              <button key={label} type="button" onClick={soon} className={cx(JUMP, 'text-left')}>
                <Icon icon={icon} size="md" />
                <span
                  className={cx(
                    'flex-1 text-label-medium',
                    label === 'Leads' ? 'text-text-tertiary' : 'text-text-primary',
                  )}
                >
                  {label}
                </span>
                {label === 'Leads' ? <span className="text-body-xsmall text-text-tertiary">Soon</span> : null}
                <Icon icon={CaretRight} size="xs" />
              </button>
            ))}
          </Card>
        </div>
      </div>

      {isAdmin && assigning ? (
        <AssignAmDialog
          open
          onClose={() => setAssigning(false)}
          businessId={business.id}
          businessName={business.name}
          place={business.city}
          currentPrimaryId={primary?.id}
          hasNoManager={business.accountManagers.length === 0}
        />
      ) : null}
    </>
  );
}
